import React from 'react';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { ButtonExperiment } from '@/components/ButtonExperiment';
import { useButtonPlaceholderHeight } from '@/components/ButtonExperiment/useButtonPlaceholderHeight';
import { ExtendedLink } from '@/components/ExtendedLink';
import { Placeholder, PlaceHolderShape } from '@/components/Placeholder';
import {
  CHECKOUT_LANDING_PATH,
  CHECKOUT_QUERY_PARAM,
} from '@/modules/checkout/constants';
import { VARIANT_IDENTIFIER } from '@/modules/experiments/config';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { type ProductBySlugResponse } from '@/modules/product/aliases';
import { isProductOnSale } from '@/modules/product/helpers/isProductOnSale';
import { isSold } from '@/modules/product/helpers/isSold';
import { type BackendProductStatus } from '@/modules/product/types';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type Props = {
  preventBuy: boolean;
  status?: BackendProductStatus | ProductBySlugResponse['status'];
  onBuyButtonClick: () => void;
  isLoading: boolean;
  className?: string;
};

export function BuyNowButton({
  status,
  preventBuy,
  onBuyButtonClick,
  isLoading,
  className,
}: Props) {
  const productT = useTranslations('product');
  const commonT = useTranslations('common');

  const [isAuthenticated] = useCurrentUser();

  const placeholderHeight = useButtonPlaceholderHeight();

  const [{ web_8651_buy_now_presignup }] = useExperiments(
    ['web_8651_buy_now_presignup'],
    { deferred: true }
  );

  const { web_thrift_ui_button } = useExperiments(['web_thrift_ui_button']);
  const isThriftButtonVariant = web_thrift_ui_button === VARIANT_IDENTIFIER;

  const isItemSold = status && isSold(status);

  const isAuthenticationRequired = !isAuthenticated && isProductOnSale(status);
  const isUserPurchaseAllowed = isAuthenticated && isProductOnSale(status);

  const isBuyNowPresignupVariant =
    web_8651_buy_now_presignup === VARIANT_IDENTIFIER;
  const loginParams = new URLSearchParams({
    titleKey: 'Contextual.Login.Title.Exp',
    redirect: CHECKOUT_LANDING_PATH,
    [CHECKOUT_QUERY_PARAM]: 'true',
  });
  if (isBuyNowPresignupVariant) {
    loginParams.set('buyNow', 'true');
  }
  const loginHref = `/login/?${loginParams.toString()}`;

  function renderContent() {
    if (isLoading) {
      return (
        <Placeholder
          shape={PlaceHolderShape.RECTANGLE}
          width="100%"
          height={placeholderHeight}
        />
      );
    }

    if (preventBuy) {
      return (
        <ButtonExperiment disabled fullWidth>
          {productT('Buy')}
        </ButtonExperiment>
      );
    }

    if (isItemSold) {
      return (
        <div
          aria-hidden
          className={clsx(styles.soldLabel, {
            [styles.soldLabelThrift]: isThriftButtonVariant,
          })}
        >
          <ButtonExperiment
            as="span"
            fullWidth
            variant="tertiary"
            legacyClassName={styles.soldLabelLegacyButton}
          >
            {commonT('SoldSingular')}
          </ButtonExperiment>
        </div>
      );
    }

    if (isAuthenticationRequired) {
      return (
        <ExtendedLink href={loginHref} onClick={onBuyButtonClick}>
          <ButtonExperiment as="span" fullWidth>
            {productT('Buy')}
          </ButtonExperiment>
        </ExtendedLink>
      );
    }

    if (isUserPurchaseAllowed) {
      return (
        <ExtendedLink href={CHECKOUT_LANDING_PATH} onClick={onBuyButtonClick}>
          <ButtonExperiment as="span" fullWidth>
            {productT('Buy')}
          </ButtonExperiment>
        </ExtendedLink>
      );
    }

    return null;
  }

  return <div className={className}>{renderContent()}</div>;
}
